import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import MessageFile from './components/MessageFile';
import { ChatSessionFile } from 'models/Message';
import api from 'api';
import InfiniteScroll from 'components/InfiniteScroll';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { NewMessageFilesEventData } from 'services/socketService/socketEventData';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Loader } from 'ncoded-component-library';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import useChatScroll from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/pages/Chat/hooks/useChatScroll';
import useBusEvent from 'hooks/useBusEvent';

import './MessageFiles.styles.scss';

const DEFAULT_PARAMS = { $limit: '20', $sort: '-createdAt' };

type MessageFilesProps = {
  className?: string;
  sessionId: string;
  dataRoomId: string;
  dataRoomName: string;
};

const MessageFiles: React.FC<MessageFilesProps> = (props) => {
  const { className, sessionId, dataRoomId, dataRoomName } = props;

  const [hasNewMessages, setHasNewMessages] = useState(true);

  const [messagesContainer, messagesContainerRef] =
    useCallbackRef<InfiniteScrollRef>();

  const classes = classNames('message-files', className);

  const { t } = useTranslation();

  const { shouldScrollTL, scrollToBottom } = useChatScroll({
    condition: hasNewMessages,
    container: messagesContainer?.container,
  });

  const {
    loading,
    items: files,
    setItems: setFiles,
    onContainerScrolled: onMessagesScroll,
  } = useInfinitePagination<ChatSessionFile>({
    condition: !!sessionId,
    resetDeps: [sessionId],
    dependencies: [sessionId],
    makeRequest: async (currentPage) =>
      api.messages
        .getMessagesFiles(sessionId, {
          $page: currentPage.toString(),
          ...DEFAULT_PARAMS,
        })
        .then((res) => res.data),
  });

  const isEmpty = useMemo(() => !files?.length, [files]);

  const scrollToView = useCallback(() => {
    setHasNewMessages(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const updateFile = useCallback(
    (file: Partial<ChatSessionFile>) =>
      setFiles((old) =>
        old.map((el) => (el._id === file._id ? { ...el, ...file } : el)),
      ),
    [setFiles],
  );

  const removeFile = useCallback(
    (fileId: string) =>
      setFiles((old) => old.filter(({ _id }) => _id !== fileId)),
    [setFiles],
  );

  const addFiles = useCallback(
    (chatSessionFiles: ChatSessionFile[]) => {
      setHasNewMessages(true);
      setFiles((old) => [...chatSessionFiles, ...old]);

      if (shouldScrollTL) scrollToView();
    },
    [scrollToView, setFiles, shouldScrollTL],
  );

  const onFileAddToDataRoom = useCallback(
    async (_id: string) => {
      await api.messages.moveFileToDataRoom(sessionId, _id);
      updateFile({ _id, isDataRoomFile: true });
    },
    [sessionId, updateFile],
  );

  const onFileRemove = useCallback(
    async (_id: string, removeFromDataRoom?: boolean) => {
      await api.messages.removeMessageFile(sessionId, _id, removeFromDataRoom);

      bus.broadcastEvent(StandardEventTypes.FILE_DELETE, _id);
      removeFile(_id);
    },
    [sessionId, removeFile],
  );

  const onFilePreview = useCallback(
    (id: string) => api.messages.previewMessageFile(sessionId, id),
    [sessionId],
  );

  const newMessageFiles = useCallback(
    ({
      sessionId: sessionIdEvent,
      chatSessionFiles,
    }: NewMessageFilesEventData) => {
      if (sessionIdEvent !== sessionId) return;

      addFiles(chatSessionFiles);
    },
    [sessionId, addFiles],
  );

  const newFilesSent = useCallback(
    (data: CallbackBusType<ChatSessionFile[]>) => {
      const { payload } = data;

      addFiles(payload);
    },
    [addFiles],
  );

  const onMessageDelete = useCallback(
    ({ payload }: CallbackBusType<{ chatSessionFileId: string }>) => {
      removeFile(payload.chatSessionFileId);
    },
    [removeFile],
  );

  useBusEvent(StandardEventTypes.FILES_SENT, newFilesSent);

  useSocketEvent(LISTEN_EVENTS.NEW_MESSAGE_FILES, newMessageFiles);

  useBusEvent(StandardEventTypes.FILE_DELETED_WITH_MESSAGE, onMessageDelete);

  if (loading && isEmpty) return null;

  return (
    <div className={classes}>
      <div className="message-files__heading">
        {t('filesInChat')}
        <p>
          {isEmpty
            ? t('noFilesInChat') + ' ' + t('uploadByDraggingFilesToScreen')
            : t('onlyViewedByMembers')}
        </p>
      </div>

      {!isEmpty && (
        <InfiniteScroll
          ref={messagesContainerRef}
          className="message-files__files"
          localScroll
          onScroll={onMessagesScroll}
        >
          {files.map(
            ({
              _id,
              isDataRoomFile,
              canMoveFileToDataRoom,
              file: { type, originalType, name, url },
            }) => (
              <MessageFile
                key={_id}
                id={_id}
                name={name}
                url={url}
                dataRoomId={dataRoomId}
                dataRoomName={dataRoomName}
                type={originalType || type}
                isDataRoomFile={isDataRoomFile}
                canMoveFileToDataRoom={canMoveFileToDataRoom}
                canRemoveFile={canMoveFileToDataRoom}
                onFilePreview={onFilePreview}
                onFileRemove={onFileRemove}
                onFileAddToDataRoom={onFileAddToDataRoom}
              />
            ),
          )}

          {loading && <Loader overlay={false} />}
        </InfiniteScroll>
      )}
    </div>
  );
};

export default MessageFiles;
